import { Box, Link as MaterialLink, Typography } from "@mui/material";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

interface PrivacyContentProps {
  size?: "default" | "compact";
}

export default function PrivacyContent({ size = "default" }: PrivacyContentProps) {
  const { t } = useTranslation();

  const headingVariant = size === "compact" ? "h6" : "h5";
  const subheadingVariant = size === "compact" ? "subtitle1" : "h5";
  const bodyVariant = size === "compact" ? "body2" : "body1";
  const gap = size === "compact" ? 1.5 : 2.5;

  const usageItems = t("privacyPage.section3.items", { returnObjects: true }) as string[];
  const paymentItems = t("privacyPage.section4.items", { returnObjects: true }) as string[];
  const sharingItems = t("privacyPage.section6.items", { returnObjects: true }) as string[];
  const section2p3 = t("privacyPage.section2.p3", { defaultValue: "" });
  const paymentFooter = t("privacyPage.section4.footer", { defaultValue: "" });
  const rightsP1 = t("privacyPage.section10.p1", { defaultValue: "" });
  const rightsP2 = t("privacyPage.section10.p2", { defaultValue: "" });
  const rightsP3 = t("privacyPage.section10.p3", { defaultValue: "" });
  const rightsP4 = t("privacyPage.section10.p4", { defaultValue: "" });
  const contactP1 = t("privacyPage.section15.p1", { defaultValue: "" });

  const renderSentenceWithEmail = (sentence: string, email: string) => {
    if (!sentence || !sentence.includes(email)) {
      return sentence;
    }
    const [before, after] = sentence.split(email);
    return (
      <Fragment>
        {before}
        <MaterialLink color="text.primary" component={RouterLink} to={`mailto:${email}`}>
          {email}
        </MaterialLink>
        {after}
      </Fragment>
    );
  };

  return (
    <Box display="flex" flexDirection="column" gap={gap} sx={{ width: "100%" }}>
      <Typography component="h2" variant={headingVariant} color="text.primary" sx={{ textAlign: "left" }}>
        {t("privacyPage.heading")}
      </Typography>
      <Typography component="h2" variant={subheadingVariant} color="text.primary" sx={{ textAlign: "left" }}>
        {t("privacyPage.lastUpdated")}
      </Typography>

      <Typography variant={bodyVariant} color="text.secondary" sx={{ textAlign: "left" }}>
        {t("privacyPage.section1.p1")}
      </Typography>
      <Typography variant={bodyVariant} color="text.secondary" sx={{ textAlign: "left" }}>
        {t("privacyPage.section1.p2")}
      </Typography>

      <Typography component="h3" variant={subheadingVariant} color="text.primary" sx={{ textAlign: "left" }}>
        {t("privacyPage.section2.title")}
      </Typography>
      <Typography variant={bodyVariant} color="text.secondary" sx={{ textAlign: "left" }}>
        {t("privacyPage.section2.p1")}
      </Typography>
      <Typography variant={bodyVariant} color="text.secondary" sx={{ textAlign: "left" }}>
        {t("privacyPage.section2.p2")}
      </Typography>
      {section2p3 && (
        <Typography variant={bodyVariant} color="text.secondary" sx={{ textAlign: "left" }}>
          {section2p3}
        </Typography>
      )}

      <Typography component="h3" variant={subheadingVariant} color="text.primary" sx={{ textAlign: "left" }}>
        {t("privacyPage.section3.title")}
      </Typography>
      <Typography component="div" variant={bodyVariant} color="text.secondary" sx={{ textAlign: "left" }}>
        {t("privacyPage.section3.intro")}
        <Box component="ul" sx={{ listStyle: "circle", pl: 3, mt: 1 }}>
          {(Array.isArray(usageItems) ? usageItems : []).map((item, index) => (
            <Box component="li" key={index} sx={{ mb: 0.5 }}>
              {item}
            </Box>
          ))}
        </Box>
      </Typography>

      <Typography component="h3" variant={subheadingVariant} color="text.primary" sx={{ textAlign: "left" }}>
        {t("privacyPage.section4.title")}
      </Typography>
      <Typography component="div" variant={bodyVariant} color="text.secondary" sx={{ textAlign: "left" }}>
        {t("privacyPage.section4.intro")}
        <Box component="ul" sx={{ listStyle: "circle", pl: 3, mt: 1 }}>
          {(Array.isArray(paymentItems) ? paymentItems : []).map((item, index) => (
            <Box component="li" key={index} sx={{ mb: 0.5 }}>
              {item}
            </Box>
          ))}
        </Box>
      </Typography>
      {paymentFooter && (
        <Typography variant={bodyVariant} color="text.secondary" sx={{ textAlign: "left" }}>
          {paymentFooter}
        </Typography>
      )}

      <Typography component="h3" variant={subheadingVariant} color="text.primary" sx={{ textAlign: "left" }}>
        {t("privacyPage.section5.title")}
      </Typography>
      <Typography variant={bodyVariant} color="text.secondary" sx={{ textAlign: "left" }}>
        {t("privacyPage.section5.p1")}
      </Typography>
      <Typography variant={bodyVariant} color="text.secondary" sx={{ textAlign: "left" }}>
        {t("privacyPage.section5.p2")}
      </Typography>
      <Typography variant={bodyVariant} color="text.secondary" sx={{ textAlign: "left" }}>
        {t("privacyPage.section5.p3")}
      </Typography>

      <Typography component="h3" variant={subheadingVariant} color="text.primary" sx={{ textAlign: "left" }}>
        {t("privacyPage.section6.title")}
      </Typography>
      <Typography component="div" variant={bodyVariant} color="text.secondary" sx={{ textAlign: "left" }}>
        {t("privacyPage.section6.intro")}
        <Box component="ul" sx={{ listStyle: "circle", pl: 3, mt: 1 }}>
          {(Array.isArray(sharingItems) ? sharingItems : []).map((item, index) => (
            <Box component="li" key={index} sx={{ mb: 0.5 }}>
              {item}
            </Box>
          ))}
        </Box>
      </Typography>

      <Typography component="h3" variant={subheadingVariant} color="text.primary" sx={{ textAlign: "left" }}>
        {t("privacyPage.section7.title")}
      </Typography>
      <Typography variant={bodyVariant} color="text.secondary" sx={{ textAlign: "left" }}>
        {t("privacyPage.section7.p1")}
      </Typography>

      <Typography component="h3" variant={subheadingVariant} color="text.primary" sx={{ textAlign: "left" }}>
        {t("privacyPage.section8.title")}
      </Typography>
      <Typography variant={bodyVariant} color="text.secondary" sx={{ textAlign: "left" }}>
        {t("privacyPage.section8.p1")}
      </Typography>

      <Typography component="h3" variant={subheadingVariant} color="text.primary" sx={{ textAlign: "left" }}>
        {t("privacyPage.section9.title")}
      </Typography>
      <Typography variant={bodyVariant} color="text.secondary" sx={{ textAlign: "left" }}>
        {t("privacyPage.section9.p1")}
      </Typography>

      <Typography component="h3" variant={subheadingVariant} color="text.primary" sx={{ textAlign: "left" }}>
        {t("privacyPage.section10.title")}
      </Typography>
      {rightsP1 && (
        <Typography variant={bodyVariant} color="text.secondary" sx={{ textAlign: "left" }}>
          {rightsP1}
        </Typography>
      )}
      {rightsP2 && (
        <Typography variant={bodyVariant} color="text.secondary" sx={{ textAlign: "left" }}>
          {rightsP2}
        </Typography>
      )}
      {rightsP3 && (
        <Typography variant={bodyVariant} color="text.secondary" sx={{ textAlign: "left" }}>
          {renderSentenceWithEmail(rightsP3, "privacy@bigswiss.ch")}
        </Typography>
      )}
      {rightsP4 && (
        <Typography variant={bodyVariant} color="text.secondary" sx={{ textAlign: "left" }}>
          {rightsP4}
        </Typography>
      )}

      <Typography component="h3" variant={subheadingVariant} color="text.primary" sx={{ textAlign: "left" }}>
        {t("privacyPage.section11.title")}
      </Typography>
      <Typography variant={bodyVariant} color="text.secondary" sx={{ textAlign: "left" }}>
        {t("privacyPage.section11.p1")}
      </Typography>

      <Typography component="h3" variant={subheadingVariant} color="text.primary" sx={{ textAlign: "left" }}>
        {t("privacyPage.section12.title")}
      </Typography>
      <Typography variant={bodyVariant} color="text.secondary" sx={{ textAlign: "left" }}>
        {t("privacyPage.section12.p1")}
      </Typography>

      <Typography component="h3" variant={subheadingVariant} color="text.primary" sx={{ textAlign: "left" }}>
        {t("privacyPage.section13.title")}
      </Typography>
      <Typography variant={bodyVariant} color="text.secondary" sx={{ textAlign: "left" }}>
        {t("privacyPage.section13.p1")}
      </Typography>

      <Typography component="h3" variant={subheadingVariant} color="text.primary" sx={{ textAlign: "left" }}>
        {t("privacyPage.section14.title")}
      </Typography>
      <Typography variant={bodyVariant} color="text.secondary" sx={{ textAlign: "left" }}>
        {t("privacyPage.section14.p1")}
      </Typography>

      <Typography component="h3" variant={subheadingVariant} color="text.primary" sx={{ textAlign: "left" }}>
        {t("privacyPage.section15.title")}
      </Typography>
      <Typography variant={bodyVariant} color="text.secondary" sx={{ textAlign: "left" }}>
        {renderSentenceWithEmail(contactP1, "privacy@bigswiss.ch")}
      </Typography>
    </Box>
  );
}
